import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import { useMutation } from 'redux-query-react';
import productInbankRentalRequest from 'data/requests/product/inbankRental/productInbankRentalRequest';
import { IConfigPrice, IProductConfig, ISelectedProductInformation } from 'components/Catalog/Product/Detail';
import { ISwatchConfig } from 'components/Catalog/Product/Configurator';
import { empty } from '../../../helpers/empty';
import { ISliceConfiguration } from 'components/Catalog/Product/DetailComponent/Slice3/Slice3ConfigReducer';
import RegularCartOption from 'components/Catalog/Product/DetailComponent/RegularCart/RegularCartOption';
import Slice3Option from 'components/Catalog/Product/DetailComponent/Slice3/Slice3Option';
import InbankRentalOption, {
    IRenewalPeriod,
} from 'components/Catalog/Product/DetailComponent/InbankRentalCartOption/InbankRentalOption';
import EstoSlice3Option, {
    IEstoSlice3Config,
} from 'components/Catalog/Product/DetailComponent/EstoSlice3/EstoSlice3Option';
import productSlice3Request from 'data/requests/product/slice3/productSlice3Request';
import { ICartTimeToRenewValue } from 'components/Catalog/Product/CartButton';

interface IInbankRentalResponse {
    monthlyFee?: { label: string; value: string };
    inbankRentalLabels?: string[];
    metaResponse?: string;
    timeToRenew?: string;
    cartHeroProducts?: ICartTimeToRenewValue[];
    modalLabels?: { [key: string]: string };
    disableOption?: boolean;
    aboutInbankRental?: string;
    inbankRentalTermsUrl?: string;
    notSamePrioritySkus?: string[];
    isHero?: boolean;
    renewalPeriods?: IRenewalPeriod[];
    productDetailViewLogo?: string;
    chooseRenewalPeriodLabel?: string;
    extraInfo?: string;
    useInsuranceLabel?: string;
    isInbankRentalProduct?: boolean;
}

export interface IInbankRentalData {
    metaResponse?: string;
    timeToRenew?: string;
    cartHeroProducts?: ICartTimeToRenewValue[];
    notSamePrioritySkus?: string[];
    isHero?: boolean;
    modalLabels?: { [key: string]: string };
    monthlyFee?: {
        label: string;
        value: string;
    };
}

interface ISlice3Response {
    body: ISliceConfiguration;
}

interface IProps {
    monthlyFee?: {
        label: string;
        value: string;
    };
    productId?: string;
    inbankRentalLabels?: string[];
    aboutInbankRental?: string;
    inbankRentalTermsUrl?: string;
    timeToRenew?: string;
    cartTimeToRenew?: number[];
    productConfig: IProductConfig;
    selectedProductInformation: ISelectedProductInformation;
    price: IConfigPrice;
    oldPrice?: string;
    metaResponse?: string;
    swatchConfig: ISwatchConfig;
    inbankRentalEnabled?: boolean;
    slice3MethodEnabled?: boolean;
    estoSlice3Config: IEstoSlice3Config;
    slice3Config?: ISliceConfiguration;
    setSlice3Config?: (config?: ISliceConfiguration) => void;
    cartFlowType: string;
    extraInfo?: string;
    useInsuranceLabel?: string;
    setCartFlowType: (value: string) => void;
    onInbankRentalDataChange?: (data: IInbankRentalData) => void;
    addToCartOrigin?: () => void;
    children?: any;
    handleBuyBackProducts?: () => void;
}

const CartWrapper = (props: IProps) => {
    const {
        monthlyFee,
        productId,
        price,
        oldPrice,
        selectedProductInformation,
        productConfig,
        inbankRentalEnabled,
        slice3MethodEnabled,
        estoSlice3Config,
        slice3Config,
        setSlice3Config,
        cartFlowType,
        setCartFlowType,
    } = props;
    const [fee, setFee] = useState(monthlyFee);
    const [disableInbankRentalOption, setDisableOption] = useState<boolean | undefined>(false);
    const [inbankRentalApiResponse, setInbankRentalApiResponse] = useState<string | undefined>(props.metaResponse);
    const [inbankRentalLabels, setInbankRentalLabels] = useState(props.inbankRentalLabels);
    const [productDetailViewLogo, setProductDetailViewLogo] = useState<string | undefined>();
    const [chooseRenewalPeriodLabel, setChooseRenewalPeriodLabel] = useState<string | undefined>();
    const [inbankRentalExtraInfo, setInbankRentalExtraInfo] = useState<string | undefined>();
    const [inbankRentalUseInsuranceLabel, setInbankRentalUseInsuranceLabel] = useState<string | undefined>();
    const [timeToRenew, setTimeToRenew] = useState<string | undefined>();
    const [renewalPeriods, setRenewalPeriods] = useState<IRenewalPeriod[]>([]);
    const [currentRenewalPeriod, setCurrentRenewalPeriod] = useState('');
    const [aboutInbankRental, setAboutInbankRental] = useState(props.aboutInbankRental);
    const [inbankRentalTermsUrl, setInbankRentalTermsUrl] = useState(props.inbankRentalTermsUrl);
    const [isInbankRentalProduct, setIsInbankRentalProduct] = useState(false);
    const [notSamePrioritySkus, setNotSamePrioritySkus] = useState<string[]>([]);
    const [isHero, setIsHero] = useState<boolean>(false);
    const [cartHeroProducts, setCartHeroProducts] = useState<ICartTimeToRenewValue[]>();
    const [inbankRentalModalFitLabels, setInbankRentalModalFitLabels] = useState<{ [key: string]: string }>({});

    const [, inbankRentalRequest] = useMutation((id: string, period: string) => productInbankRentalRequest(id, period));
    const buttonConf = productConfig.addToCartButton;
    const isInterest =
        buttonConf &&
        typeof buttonConf === 'object' &&
        'interestConfig' in buttonConf &&
        (buttonConf as any).interestConfig !== undefined;

    const [, slice3Request] = useMutation((id: string) => productSlice3Request(id));

    useEffect(() => {
        if (productId && slice3MethodEnabled && setSlice3Config) {
            void slice3Request(productId).then((response: ISlice3Response) => {
                setSlice3Config(response.body);
            });
        }
    }, [productId, slice3MethodEnabled, setSlice3Config]);

    const RequestInbankRental = () => {
        if (productId && !empty(inbankRentalEnabled)) {
            if (productId.toString() === productConfig.id.toString() && !empty(productConfig.isConfigurableProduct)) {
                // Do not query configurable data
                return;
            }
            void inbankRentalRequest(productId, currentRenewalPeriod).then(
                (response: { body: IInbankRentalResponse }) => {
                    setFee(response.body?.monthlyFee);
                    setInbankRentalLabels(response.body?.inbankRentalLabels);
                    setInbankRentalApiResponse(response.body?.metaResponse);
                    setTimeToRenew(response.body?.timeToRenew);
                    setCartHeroProducts(response.body?.cartHeroProducts);
                    setInbankRentalModalFitLabels(response.body?.modalLabels ?? {});
                    setDisableOption(response.body?.disableOption);
                    setAboutInbankRental(response.body?.aboutInbankRental);
                    setInbankRentalTermsUrl(response.body?.inbankRentalTermsUrl);
                    setNotSamePrioritySkus(response.body?.notSamePrioritySkus ?? []);
                    setIsHero(response.body?.isHero ?? false);
                    setRenewalPeriods(response.body?.renewalPeriods ?? []);
                    setProductDetailViewLogo(response.body?.productDetailViewLogo);
                    setChooseRenewalPeriodLabel(response.body?.chooseRenewalPeriodLabel);
                    setInbankRentalExtraInfo(response.body?.extraInfo);
                    setInbankRentalUseInsuranceLabel(response.body?.useInsuranceLabel);
                    setIsInbankRentalProduct(response.body?.isInbankRentalProduct ?? false);
                    if (
                        !currentRenewalPeriod &&
                        response.body?.renewalPeriods &&
                        response.body?.renewalPeriods.length > 0 &&
                        response.body?.renewalPeriods[0].value !== undefined
                    ) {
                        setCurrentRenewalPeriod(response.body?.renewalPeriods[0].value ?? '');
                    }
                },
            );
        }
    };

    useEffect(() => {
        RequestInbankRental();
    }, [productId, inbankRentalEnabled, currentRenewalPeriod]);

    useEffect(() => {
        window.addEventListener('hasInsuranceChanged', RequestInbankRental);
        return function cleanup() {
            window.removeEventListener('hasInsuranceChanged', RequestInbankRental);
        };
    });

    useEffect(() => {
        if (!empty(timeToRenew)) {
            window.dispatchEvent(
                new CustomEvent('financial-calculator-default-period-set', {
                    detail: {
                        defaultPeriod: timeToRenew,
                    },
                }),
            );
        }
    }, [timeToRenew]);

    const isOnlyOneMethod = !inbankRentalEnabled && !slice3MethodEnabled;

    // Expose Inbank rental data for use by parent components
    const getInbankRentalData = (): IInbankRentalData => ({
        metaResponse: inbankRentalApiResponse,
        timeToRenew,
        cartHeroProducts,
        notSamePrioritySkus,
        isHero,
        modalLabels: inbankRentalModalFitLabels,
        monthlyFee: fee,
    });

    // Make the data available to parent component via callback
    React.useEffect(() => {
        if (props.onInbankRentalDataChange && inbankRentalEnabled) {
            props.onInbankRentalDataChange(getInbankRentalData());
        }
    }, [
        inbankRentalApiResponse,
        timeToRenew,
        cartHeroProducts,
        notSamePrioritySkus,
        isHero,
        inbankRentalModalFitLabels,
        fee,
    ]);

    return (
        <React.Fragment>
            <ul className="product-pricing__options">
                <InbankRentalOption
                    cartFlowType={cartFlowType}
                    setCartFlowType={setCartFlowType}
                    isInterest={isInterest}
                    aboutInbankRental={aboutInbankRental}
                    disableInbankRentalOption={disableInbankRentalOption}
                    inbankRentalEnabled={inbankRentalEnabled}
                    inbankRentalLabels={inbankRentalLabels}
                    fee={fee}
                    inbankRentalTermsUrl={inbankRentalTermsUrl}
                    renewalPeriods={renewalPeriods}
                    setRenewalPeriod={setCurrentRenewalPeriod}
                    productDetailViewLogo={productDetailViewLogo}
                    chooseRenewalPeriodLabel={chooseRenewalPeriodLabel}
                    selectedRenewalPeriod={currentRenewalPeriod}
                    useInsuranceLabel={inbankRentalUseInsuranceLabel}
                    extraInfo={inbankRentalExtraInfo}
                    isInbankRentalProduct={isInbankRentalProduct}
                />
                <RegularCartOption
                    isInterest={isInterest}
                    isOnlyOneMethod={isOnlyOneMethod}
                    productConfig={productConfig}
                    cartFlowType={cartFlowType}
                    setCartFlowType={setCartFlowType}
                    price={price}
                    oldPrice={oldPrice}
                    selectedProductInformation={selectedProductInformation}
                />
                <Slice3Option
                    isInterest={isInterest}
                    productId={productId}
                    setCartFlowType={setCartFlowType}
                    productConfig={productConfig}
                    cartFlowType={cartFlowType}
                    selectedProductInformation={selectedProductInformation}
                    slice3Config={slice3Config}
                    slice3MethodEnabled={slice3MethodEnabled}
                />
                <EstoSlice3Option
                    isInterest={isInterest}
                    productId={productId}
                    setCartFlowType={setCartFlowType}
                    cartFlowType={cartFlowType}
                    estoSlice3Config={estoSlice3Config}
                />
            </ul>
            {props.children}
        </React.Fragment>
    );
};

export default memo(CartWrapper);
